import {
  Globe,
  Star,
  MessageSquare,
  Users,
  BookMarked,
  CalendarDays,
  Video,
  ArrowRight,
  Shield,
  Briefcase,
  TrendingUp,
  Zap,
  Award,
  Network,
  Bot,
  BarChart3,
  Store,
} from 'lucide-react';

const APP_URL = 'https://app.mspcollective.io';

const FEATURES = [
  {
    icon: Star,
    title: 'Vendor Ratings & Reviews',
    description: 'Honest ratings from real MSP operators. Review scores are never influenced by vendor tier. Vendors earn their stars from the people who actually use them.',
  },
  {
    icon: MessageSquare,
    title: 'Community Forums',
    description: 'Topic-organized forums where MSPs share war stories, technical solutions, and business advice. No vendor fluff allowed.',
  },
  {
    icon: Briefcase,
    title: 'Job Board',
    description: 'MSP-specific job listings. Whether you are hiring or looking for your next role, connect with companies that speak your language.',
  },
  {
    icon: Users,
    title: 'Peer Networking',
    description: 'Connect with other MSPs by specialty, size, or geography. Find a referral partner, a mentor, or your next hire.',
  },
  {
    icon: BookMarked,
    title: 'Learning & Courses',
    description: 'Vendor-created hubs with courses, certifications, and learning paths. Upskill your team with content built for MSPs.',
  },
  {
    icon: CalendarDays,
    title: 'Event Calendar',
    description: 'Conferences, webinars, and local meetups aggregated in one place. Create your own events or RSVP to community ones.',
  },
  {
    icon: Video,
    title: 'Live Streaming',
    description: 'Watch vendor demos, community presentations, and expert sessions live. Go live yourself and share your expertise.',
  },
  {
    icon: Shield,
    title: 'Threat Intelligence',
    description: 'Community-sourced IOCs, detection rules, and war rooms for active incidents. Stay ahead of threats with your peers.',
  },
];

const ADVANCED_FEATURES = [
  {
    icon: Bot,
    title: 'AI-Powered Sage',
    description: 'Ask Sage anything about running an MSP. Powered by real community knowledge, not generic AI.',
  },
  {
    icon: BarChart3,
    title: 'Anonymized Benchmarking',
    description: 'See how your pricing, salaries, and margins compare to peers your size. All data is anonymized.',
  },
  {
    icon: Store,
    title: 'Script Marketplace',
    description: 'Share and discover PowerShell, Python, and automation scripts from the community.',
  },
  {
    icon: Network,
    title: 'Serendipity Engine',
    description: 'Get matched with MSPs you should know. Coffee chat suggestions based on shared interests and challenges.',
  },
  {
    icon: Award,
    title: 'Certifications & Badges',
    description: 'Track your certs, earn community badges, and showcase your expertise on your public profile.',
  },
  {
    icon: TrendingUp,
    title: 'Tech Stack Sharing',
    description: 'See what tools MSPs your size are actually using. Compare stacks and get real recommendations.',
  },
];

const SOCIAL_PROOF = [
  { metric: '47', label: 'Feature Modules', sublabel: 'built and deployed' },
  { metric: '100%', label: 'Free for MSPs', sublabel: 'no hidden costs' },
  { metric: '5', label: 'Auth Methods', sublabel: 'password, MFA, magic link, Microsoft, invite' },
  { metric: '24/7', label: 'AI Sage', sublabel: 'always-on MSP advisor' },
];

export function LandingPage() {
  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100">
      {/* Nav */}
      <nav className="sticky top-0 z-50 border-b border-zinc-800/50 bg-zinc-950/80 backdrop-blur-lg">
        <div className="mx-auto max-w-7xl flex items-center justify-between px-6 py-4">
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-cyan-600">
              <Globe className="h-5 w-5 text-white" />
            </div>
            <span className="text-lg font-semibold">MSP Collective</span>
          </div>
          <div className="hidden md:flex items-center gap-6">
            <a href="#features" className="text-sm text-zinc-400 hover:text-zinc-100 transition-colors">Features</a>
            <a href="#faq" className="text-sm text-zinc-400 hover:text-zinc-100 transition-colors">FAQ</a>
            <a href={`${APP_URL}/login`} className="text-sm text-zinc-400 hover:text-zinc-100 transition-colors">Sign In</a>
          </div>
          <a
            href={`${APP_URL}/register`}
            className="inline-flex items-center gap-2 rounded-lg bg-cyan-600 px-4 py-2 text-sm font-medium text-white hover:bg-cyan-500 transition-colors"
          >
            Join Free <ArrowRight className="h-4 w-4" />
          </a>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-cyan-600/10 via-transparent to-transparent" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_rgba(8,145,178,0.15),_transparent_50%)]" />
        <div className="relative mx-auto max-w-7xl px-6 py-24 text-center lg:py-36">
          <div className="inline-flex items-center gap-2 rounded-full bg-cyan-600/10 border border-cyan-600/20 px-4 py-1.5 text-sm text-cyan-400 mb-8">
            <Zap className="h-3.5 w-3.5" />
            Free for MSPs. Always.
          </div>
          <h1 className="text-5xl font-bold tracking-tight sm:text-6xl lg:text-7xl">
            The community built{' '}
            <span className="text-cyan-400">by MSPs, for MSPs</span>
          </h1>
          <p className="mx-auto mt-6 max-w-2xl text-lg text-zinc-400 leading-relaxed">
            Vendor ratings from real operators, peer networking, job board, learning courses, threat intelligence, and AI-powered tools — all in one place, all free for MSPs.
          </p>
          <div className="mt-10 flex items-center justify-center gap-4 flex-wrap">
            <a
              href={`${APP_URL}/register`}
              className="inline-flex items-center gap-2 rounded-lg bg-cyan-600 px-8 py-3.5 font-medium text-white hover:bg-cyan-500 transition-colors shadow-lg shadow-cyan-600/20"
            >
              Join Free <ArrowRight className="h-4 w-4" />
            </a>
            <a
              href={`${APP_URL}/login`}
              className="inline-flex items-center gap-2 rounded-lg border border-zinc-700 px-8 py-3.5 font-medium text-zinc-300 hover:bg-zinc-800 transition-colors"
            >
              Sign In
            </a>
          </div>
          <p className="mt-4 text-sm text-zinc-500">
            No credit card required. Sign up in 30 seconds.
          </p>
        </div>
      </section>

      {/* Social Proof Bar */}
      <section className="border-y border-zinc-800/50 bg-zinc-900/30">
        <div className="mx-auto max-w-7xl px-6 py-10">
          <div className="grid grid-cols-2 gap-8 sm:grid-cols-4">
            {SOCIAL_PROOF.map((item) => (
              <div key={item.label} className="text-center">
                <div className="text-3xl font-bold text-cyan-400">{item.metric}</div>
                <div className="text-sm font-medium text-zinc-200 mt-1">{item.label}</div>
                <div className="text-xs text-zinc-500 mt-0.5">{item.sublabel}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Core Features */}
      <section id="features" className="mx-auto max-w-7xl px-6 py-24">
        <div className="text-center mb-16">
          <h2 className="text-3xl font-bold sm:text-4xl">Everything MSPs need in one community</h2>
          <p className="mt-4 text-lg text-zinc-400">
            No vendor-controlled forums. No pay-to-play. Just real MSPs helping each other.
          </p>
        </div>
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {FEATURES.map((feature) => (
            <div
              key={feature.title}
              className="group rounded-xl border border-zinc-800 bg-zinc-900/50 p-6 backdrop-blur transition-all hover:border-cyan-600/30 hover:bg-zinc-900/80"
            >
              <div className="mb-4 flex h-10 w-10 items-center justify-center rounded-lg bg-cyan-600/10 group-hover:bg-cyan-600/20 transition-colors">
                <feature.icon className="h-5 w-5 text-cyan-400" />
              </div>
              <h3 className="mb-2 font-semibold">{feature.title}</h3>
              <p className="text-sm text-zinc-400 leading-relaxed">{feature.description}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Advanced Features */}
      <section className="bg-zinc-900/30 border-y border-zinc-800/50">
        <div className="mx-auto max-w-7xl px-6 py-24">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold sm:text-4xl">Plus advanced tools built for the MSP workflow</h2>
            <p className="mt-4 text-lg text-zinc-400">
              Beyond the basics. These are the tools that give you an edge.
            </p>
          </div>
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {ADVANCED_FEATURES.map((feature) => (
              <div
                key={feature.title}
                className="flex items-start gap-4 rounded-xl border border-zinc-800 bg-zinc-900/50 p-6"
              >
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-cyan-600/10">
                  <feature.icon className="h-5 w-5 text-cyan-400" />
                </div>
                <div>
                  <h3 className="font-semibold mb-1">{feature.title}</h3>
                  <p className="text-sm text-zinc-400 leading-relaxed">{feature.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Join Your Peers CTA */}
      <section className="mx-auto max-w-7xl px-6 py-24">
        <div className="relative rounded-2xl border border-cyan-600/20 bg-gradient-to-br from-cyan-600/10 via-zinc-900 to-zinc-900 p-12 text-center overflow-hidden">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_rgba(8,145,178,0.1),_transparent_60%)]" />
          <div className="relative">
            <h2 className="text-3xl font-bold sm:text-4xl">Join your peers</h2>
            <p className="mt-4 max-w-lg mx-auto text-lg text-zinc-400">
              MSP Collective is where the real conversations happen. No vendor noise. No pay-to-play rankings. Just MSPs helping MSPs.
            </p>
            <div className="mt-8 flex items-center justify-center gap-4 flex-wrap">
              <a
                href={`${APP_URL}/register`}
                className="inline-flex items-center gap-2 rounded-lg bg-cyan-600 px-8 py-3.5 font-medium text-white hover:bg-cyan-500 transition-colors shadow-lg shadow-cyan-600/20"
              >
                Join Free <ArrowRight className="h-4 w-4" />
              </a>
              <a
                href={`${APP_URL}/login`}
                className="inline-flex items-center gap-2 rounded-lg border border-zinc-700 px-8 py-3.5 font-medium text-zinc-300 hover:bg-zinc-800 transition-colors"
              >
                Already a member? Sign In
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="mx-auto max-w-3xl px-6 py-24">
        <h2 className="text-3xl font-bold text-center mb-12">Frequently asked questions</h2>
        <div className="space-y-6">
          <div className="border-b border-zinc-800 pb-6">
            <h3 className="font-semibold text-zinc-100 mb-2">Is MSP Collective really free?</h3>
            <p className="text-sm text-zinc-400 leading-relaxed">
              Yes. MSP Collective is completely free for all MSPs, forever. No credit card, no trial period, no hidden tiers. Every feature — forums, vendor reviews, job board, threat intel, AI Sage, benchmarking, and more — is included from day one.
            </p>
          </div>
          <div className="border-b border-zinc-800 pb-6">
            <h3 className="font-semibold text-zinc-100 mb-2">Do I need to be part of The One Stack to use Collective?</h3>
            <p className="text-sm text-zinc-400 leading-relaxed">
              No. MSP Collective is a fully standalone platform at mspcollective.io with its own accounts and authentication. You do not need any other product or account to join.
            </p>
          </div>
          <div className="border-b border-zinc-800 pb-6">
            <h3 className="font-semibold text-zinc-100 mb-2">How are vendor reviews kept honest?</h3>
            <p className="text-sm text-zinc-400 leading-relaxed">
              Vendor review scores are never influenced by how much a vendor pays. Vendors cannot pay for higher ratings, remove negative reviews, or buy featured placement in review results. Period.
            </p>
          </div>
          <div className="border-b border-zinc-800 pb-6">
            <h3 className="font-semibold text-zinc-100 mb-2">What authentication methods are supported?</h3>
            <p className="text-sm text-zinc-400 leading-relaxed">
              Email/password, magic link (passwordless email), Microsoft 365 SSO, two-factor authentication (TOTP), and invite-based registration. All accounts support MFA with TOTP authenticator apps and backup codes.
            </p>
          </div>
          <div className="pb-6">
            <h3 className="font-semibold text-zinc-100 mb-2">Who is MSP Collective for?</h3>
            <p className="text-sm text-zinc-400 leading-relaxed">
              MSP Collective is built for MSP owners, operators, engineers, and anyone working inside a managed service provider. If you run, build, or work at an MSP, this community is for you.
            </p>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="border-t border-zinc-800/50 bg-zinc-900/30">
        <div className="mx-auto max-w-7xl px-6 py-16 text-center">
          <h2 className="text-2xl font-bold sm:text-3xl">Ready to join the MSP community?</h2>
          <p className="mt-3 text-zinc-400">Create your free account in 30 seconds. No credit card required.</p>
          <div className="mt-6">
            <a
              href={`${APP_URL}/register`}
              className="inline-flex items-center gap-2 rounded-lg bg-cyan-600 px-8 py-3 font-medium text-white hover:bg-cyan-500 transition-colors"
            >
              Join Free <ArrowRight className="h-4 w-4" />
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-zinc-800">
        <div className="mx-auto max-w-7xl px-6 py-12">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-cyan-600">
                <Globe className="h-4 w-4 text-white" />
              </div>
              <span className="font-semibold">MSP Collective</span>
            </div>
            <div className="flex items-center gap-6 text-sm text-zinc-500">
              <a href={`${APP_URL}/privacy`} className="hover:text-zinc-300 transition-colors">Privacy</a>
              <a href={`${APP_URL}/terms`} className="hover:text-zinc-300 transition-colors">Terms</a>
              <a href={`${APP_URL}/guidelines`} className="hover:text-zinc-300 transition-colors">Guidelines</a>
              <a href="/cookies" className="hover:text-zinc-300 transition-colors">Cookies</a>
            </div>
            <p className="text-sm text-zinc-500">
              &copy; {new Date().getFullYear()} MSP Collective
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
